import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import type { ColumnDef } from '@tanstack/react-table';
import { TanTable, SELECT_COLUMN_ID } from '../../components/TanTable';
import EdiInvoiceBucketPanel from '../../components/EdiInvoiceBucketPanel';
import Toggle from '../../components/Toggle';
import AlertModal from '../../components/ui/AlertModal';
import { WarningConfirmModal } from '../../components/ui/WarningConfirmModal';
import EdiInvoiceFileViewer from '../../components/EdiInvoiceFileViewer';
import { openProSearch } from '../../utils/proSearch';
import { invoiceListPath } from '../../services/invoiceList.service';
import ProDetailSlideOver from '../../components/pro/ProDetailSlideOver';
import {
  EdiBucketValidation,
  EdiInvoiceBucket,
  EdiInvoiceResultsResponse,
  EdiInvoiceRow,
  EdiRemoveResponse,
  EdiPreviewResponse,
  EdiSendBundle,
  EdiSendResponse,
  getBucketValidation,
  getInvoiceBucketResults,
  getInvoiceBuckets,
  money,
  previewEdi,
  removeFromEdi,
  sendEdi,
} from '../../services/pendingEdiInvoices.service';
import { useVisibilityAwareInterval } from '../../hooks/useVisibilityAwareInterval';

/**
 * EDI Pending Invoices (Accounting)
 * --------------------------------
 * The entry point into EDI invoicing: every invoice that is ready to go out over EDI and
 * has not been sent, bucketed by trading partner. Replaces V1's Accounting-dashboard
 * panel (`dash_Acct_PendingEDIInvoice`) and the Send EDI screen it opened
 * (`lib_invoice.pl::ediinvoice`).
 *
 * ACTIONS. Send EDI builds the Kleinschmidt invoice file, archives it, transmits it and
 * marks the invoices sent. Remove EDI (donotsend='Y') takes an invoice out of the queue
 * instead. Both block and refresh rather than updating optimistically — see `doSend`.
 *
 * Clicking a pro opens the order drawer, as it does everywhere else — unless the invoice
 * folds several pros, in which case it opens one search holding all of them rather than a
 * tab each. The background refresh pauses while the drawer is open and fires once on
 * close, so an edit made in there lands on the grid immediately.
 *
 * View File builds the same file and shows it WITHOUT sending: no control number is
 * claimed, nothing is archived, no invoice is marked, so it can be repeated and always
 * gives the same bytes. Outside production a Send is held anyway unless the partner has
 * Send From Non-Production ticked — but a held Send still claims a number and archives,
 * so View is the honest way to look at output, not a side effect of a held send.
 *
 * WHY THE ERRORS COLUMN IS THE POINT. V1 ran its validation one invoice at a time, four
 * queries per row; on the larger buckets that is thousands of queries per page load, so
 * the screen was slow enough that people avoided it. The backend batches all of it per
 * bucket (see edi_invoice_validate), which is what makes it reasonable to show the
 * reasons inline and to filter down to just the blocked invoices.
 *
 * NO REFRESH BUTTON — the page keeps itself current, like the dashboards. Two rules make
 * that safe rather than annoying:
 *
 *   1. A background load only calls setState when the data actually CHANGED
 *      (`bucketsEqual` / `resultsEqual`). Handing React a fresh-but-identical array on
 *      every tick is what makes a live page flash.
 *   2. A refresh never disturbs a selection in progress. TanTable PRUNES its row
 *      selection when `data` changes rather than clearing it, so a tick (or the "Errors
 *      only" toggle, which filters `data` here) keeps every row that is still on screen
 *      ticked and drops only what actually left the bucket.
 *
 * Clearing the selection goes through `clearSelection`, never `setSelectedIds([])`: the
 * table owns the checkbox state, so resetting only this page's copy leaves the boxes
 * ticked while the buttons think nothing is selected.
 */

const LS_HIDE_EMPTY = 'pending-edi-invoices-hide-empty';
// Background refresh cadence, matching Pending EDI Orders and the LC dashboards.
// Visibility-aware: pauses on a hidden tab and fires immediately on resume.
const REFRESH_MS = 30_000;

/** Order is stable from the API, so index comparison holds. */
const bucketsEqual = (a: EdiInvoiceBucket[], b: EdiInvoiceBucket[]): boolean =>
  a.length === b.length &&
  a.every((x, i) => x.key === b[i].key && x.count === b[i].count && x.label === b[i].label);

/** Everything the grid renders, so a change the user can see always re-renders. */
const resultsEqual = (
  a: EdiInvoiceResultsResponse | null,
  b: EdiInvoiceResultsResponse,
): boolean =>
  !!a &&
  a.bucket.key === b.bucket.key &&
  a.total === b.total &&
  a.totalamount === b.totalamount &&
  a.totalclean === b.totalclean &&
  a.storage_excluded === b.storage_excluded &&
  a.data.every((x, i) => {
    const y = b.data[i];
    return (
      y &&
      x.invhistoryrecno === y.invhistoryrecno &&
      x.errors === y.errors &&
      x.resend === y.resend &&
      x.invoicetotal === y.invoicetotal &&
      x.invoicedate === y.invoicedate
    );
  });

/**
 * What happened to the Intelligent Audit paperwork.
 *
 * A send is routinely hundreds of invoices and every one of them has a bundle, so the
 * happy path is ONE line with a count. What gets named individually is only what needs
 * acting on: a bundle that failed to send (the invoice file itself already went, so this
 * is a real gap the auditor will notice), and one that went out missing a document
 * because a source PDF would not merge.
 */
const PaperworkSummary: React.FC<{ bundles: EdiSendBundle[] }> = ({ bundles }) => {
  const failed = bundles.filter((b) => b.error);
  const short = bundles.filter((b) => !b.error && b.skippedDocuments.length > 0);
  const sent = bundles.filter((b) => b.transmitted).length;
  const held = bundles.length - sent - failed.length;
  const pages = bundles.reduce((n, b) => n + b.pageCount, 0);

  return (
    <div className="space-y-0.5">
      <p className={failed.length ? 'text-red-700 dark:text-red-300' : 'text-muted'}>
        Paperwork: {bundles.length} bundle{bundles.length === 1 ? '' : 's'}, {pages} page
        {pages === 1 ? '' : 's'}
        {sent > 0 && ` · ${sent} sent`}
        {held > 0 && ` · ${held} built but not sent`}
        {failed.length > 0 && ` · ${failed.length} FAILED`}
      </p>
      {failed.map((b) => (
        <p key={b.filename} className="text-red-700 dark:text-red-300">
          {b.invoicenumber}: paperwork not delivered — {b.error}
        </p>
      ))}
      {short.map((b) => (
        <p key={b.filename} className="text-amber-800 dark:text-amber-300">
          {b.invoicenumber}: sent without {b.skippedDocuments.join(', ')} — the source
          PDF would not merge
        </p>
      ))}
    </div>
  );
};

const PendingEdiInvoices: React.FC = () => {
  const [buckets, setBuckets] = useState<EdiInvoiceBucket[]>([]);
  // Which buckets hold invoices that would be REFUSED. Fetched separately from the
  // counts because the sweep validates every pending invoice and takes seconds; the
  // list paints first and recolours when this lands.
  const [validation, setValidation] = useState<Map<string, EdiBucketValidation>>(new Map());
  const [bucketsLoading, setBucketsLoading] = useState(true);
  const [bucketsError, setBucketsError] = useState<string | null>(null);

  const [selectedBucket, setSelectedBucket] = useState<string | null>(null);
  const [results, setResults] = useState<EdiInvoiceResultsResponse | null>(null);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [resultsError, setResultsError] = useState<string | null>(null);

  const [hideEmpty, setHideEmpty] = useState<boolean>(
    () => localStorage.getItem(LS_HIDE_EMPTY) === 'true',
  );
  const [errorsOnly, setErrorsOnly] = useState(false);

  // Selection is held as ids, not row objects: a background load can replace the row
  // objects, and re-deriving from ids means the buttons never act on a stale row.
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // TanTable owns the checkbox state, so clearing our copy is not enough to untick the
  // boxes — bumping this tells the table to clear too (see its selectionResetKey).
  const [selectionResetKey, setSelectionResetKey] = useState(0);
  const clearSelection = useCallback(() => {
    setSelectedIds([]);
    setSelectionResetKey((n) => n + 1);
  }, []);

  const [confirmOpen, setConfirmOpen] = useState(false);
  // One modal for every alert on the page, so each carries its own title — it used to say
  // "Cannot remove" whatever had gone wrong, including a preview or a send.
  const [notice, setNotice] = useState<{ title: string; message: React.ReactNode } | null>(null);
  const [removing, setRemoving] = useState(false);
  const [removeResult, setRemoveResult] = useState<EdiRemoveResponse | null>(null);
  const [sendResult, setSendResult] = useState<EdiSendResponse | null>(null);
  const [sending, setSending] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false);
  const [preview, setPreview] = useState<EdiPreviewResponse | null>(null);
  const [drawerPro, setDrawerPro] = useState<number | null>(null);

  useEffect(() => {
    localStorage.setItem(LS_HIDE_EMPTY, String(hideEmpty));
  }, [hideEmpty]);

  const loadBuckets = useCallback(async (background = false) => {
    if (!background) setBucketsLoading(true);
    try {
      const res = await getInvoiceBuckets();
      setBuckets((prev) => (bucketsEqual(prev, res.data) ? prev : res.data));
      setBucketsError(null);
    } catch (e: any) {
      setBucketsError(e?.response?.data?.detail || e?.message || 'Failed to load buckets');
    } finally {
      setBucketsLoading(false);
    }
  }, []);

  // Never surfaces an error of its own: this only ADDS colour to a list that is already
  // correct without it, so a failed sweep should leave the page exactly as it was rather
  // than putting a banner over a working screen. The stale map is kept for the same
  // reason a failed background poll keeps its rows.
  const loadValidation = useCallback(async () => {
    try {
      const res = await getBucketValidation();
      setValidation(new Map(res.data.map((v) => [v.key, v])));
    } catch {
      /* colour only — the counts stand on their own */
    }
  }, []);

  const loadResults = useCallback(async (key: string, background = false) => {
    if (!background) {
      setResultsLoading(true);
      setResultsError(null);
    }
    try {
      const res = await getInvoiceBucketResults(key);
      setResults((prev) => (resultsEqual(prev, res) ? prev : res));
      if (background) setResultsError(null);
    } catch (e: any) {
      // A failed BACKGROUND poll leaves the last good list on screen rather than
      // blanking it — the data is still true, it just stopped being refreshed.
      if (!background) {
        setResults(null);
        setResultsError(e?.response?.data?.detail || e?.message || 'Failed to load invoices');
      }
    } finally {
      if (!background) setResultsLoading(false);
    }
  }, []);

  useEffect(() => { loadBuckets(); }, [loadBuckets]);
  useEffect(() => { loadValidation(); }, [loadValidation]);

  // The one heartbeat: counts and, when one is open, the invoice list. Safe to run with
  // rows selected because the table prunes its selection instead of clearing it.
  // Paused while the order drawer is open: the grid is behind it, and a tick that
  // reshuffled the row you came from is disorienting at best. Closing refreshes once,
  // so an edit made in the drawer is on screen immediately rather than up to 30s later.
  useVisibilityAwareInterval(() => {
    loadBuckets(true);
    loadValidation();
    if (selectedBucket) loadResults(selectedBucket, true);
  }, drawerPro === null ? REFRESH_MS : null);

  const closeProDrawer = useCallback(() => {
    setDrawerPro(null);
    loadBuckets(true);
    loadValidation();
    if (selectedBucket) loadResults(selectedBucket, true);
  }, [selectedBucket, loadBuckets, loadResults, loadValidation]);

  const openBucket = useCallback((key: string) => {
    setSelectedBucket(key);
    setErrorsOnly(false);
    clearSelection();
    setRemoveResult(null);
    setSendResult(null);
    setPreview(null);
    loadResults(key);
  }, [loadResults, clearSelection]);

  const rows = useMemo(
    () => (errorsOnly ? (results?.data ?? []).filter((r) => r.errors > 0) : results?.data ?? []),
    [results, errorsOnly],
  );

  const selectedRows = useMemo(() => {
    const ids = new Set(selectedIds);
    return rows.filter((r) => ids.has(String(r.invhistoryrecno)));
  }, [rows, selectedIds]);

  const selectedTotal = useMemo(
    () => selectedRows.reduce((s, r) => s + r.invoicetotal, 0),
    [selectedRows],
  );

  const errorCount = useMemo(
    () => (results?.data ?? []).filter((r) => r.errors > 0).length,
    [results],
  );

  // V1's checkRemoveEDI(): a resend flag is a decision made on the Invoice screen, and
  // only that screen may clear it. Checked here for the message and again on the server,
  // which is what actually protects the row.
  const requestRemove = useCallback(() => {
    const flagged = selectedRows.filter((r) => r.resend === 'Y');
    if (flagged.length) {
      setNotice({
        title: 'Cannot remove',
        message: (
          <>
            You cannot remove invoices that are marked to resend. You can only cancel a
            Resend from the Invoice screen.
            <div className="mt-2 text-xs text-muted">
              {flagged.length} selected invoice{flagged.length === 1 ? '' : 's'}:{' '}
              {flagged.map((r) => r.invoicenumber).join(', ')}
            </div>
          </>
        ),
      });
      return;
    }
    setConfirmOpen(true);
  }, [selectedRows]);

  // Build the file and SHOW it. Nothing is claimed, archived or marked, so it can be
  // repeated — this is what to compare against V1, not whatever a held test send left in
  // the archive. It opens a viewer rather than downloading because looking at the file is
  // the whole point, and a downloaded .csv gets quarantined by macOS before you can.
  // Download is still offered, from inside the viewer.
  const doPreview = useCallback(async () => {
    if (!selectedBucket) return;
    setDownloading(true);
    try {
      const res = await previewEdi(
        selectedBucket,
        selectedRows.map((r) => r.invhistoryrecno),
      );
      if (!res.files.length) {
        // Name each invoice and why. A bare "nothing to build" is what prod invoice
        // 3160722 showed, and it gave the biller nothing to act on.
        setNotice({
          title: 'Nothing to build',
          message: res.blocked.length ? (
            <>
              None of the selected invoices can go in the file:
              <ul className="mt-2 space-y-1 text-xs">
                {res.blocked.map((b) => (
                  <li key={b.invhistoryrecno}>
                    <span className="font-semibold">{b.invoicenumber}</span>: {b.reasons.join('; ')}
                  </li>
                ))}
              </ul>
            </>
          ) : 'Nothing to build from the selected invoices.',
        });
        return;
      }
      setSendResult(null);
      setPreview(res);
    } catch (e: any) {
      setNotice({
        title: 'Preview failed',
        message: e?.response?.data?.detail || e?.message || 'Failed to build the file.',
      });
    } finally {
      setDownloading(false);
    }
  }, [selectedBucket, selectedRows]);

  // Send blocks until the file has gone. V1 locks the page and refreshes with the sent
  // rows removed; a toast that lands before the FTP does would report something the
  // system does not yet know. The button says "Sending…" and stays disabled throughout.
  const doSend = useCallback(async () => {
    if (!selectedBucket) return;
    setConfirmSend(false);
    setSending(true);
    setRemoveResult(null);
    try {
      const res = await sendEdi(
        selectedBucket,
        selectedRows.map((r) => r.invhistoryrecno),
      );
      setSendResult(res);
      clearSelection();
      // Both sides move: the sent invoices leave this bucket and its count drops.
      await Promise.all([loadResults(selectedBucket), loadBuckets(true)]);
    } catch (e: any) {
      setNotice({
        title: 'Send failed',
        message: e?.response?.data?.detail || e?.message || 'Failed to send the invoices.',
      });
    } finally {
      setSending(false);
    }
  }, [selectedBucket, selectedRows, loadResults, loadBuckets, clearSelection]);

  const doRemove = useCallback(async () => {
    if (!selectedBucket) return;
    setConfirmOpen(false);
    setRemoving(true);
    try {
      const res = await removeFromEdi(
        selectedBucket,
        selectedRows.map((r) => r.invhistoryrecno),
      );
      setRemoveResult(res);
      clearSelection();
      // Both sides move: the removed invoices leave this bucket and its count drops.
      await Promise.all([loadResults(selectedBucket), loadBuckets(true)]);
    } catch (e: any) {
      setNotice({
        title: 'Remove failed',
        message: e?.response?.data?.detail || e?.message || 'Failed to remove the invoices.',
      });
    } finally {
      setRemoving(false);
    }
  }, [selectedBucket, selectedRows, loadResults, loadBuckets, clearSelection]);

  // V1's column set, near enough V1's order — Accounting reads this screen left to right
  // every day. The one change: Pro # sits right after Invoice Date instead of trailing the
  // row, because it is the second thing anyone looks for. Invoice # is pinned left and the
  // selection checkboxes right (see lockedColumns below).
  const columns = useMemo<ColumnDef<EdiInvoiceRow>[]>(() => [
    {
      accessorKey: 'invoicenumber',
      header: 'Invoice #',
      size: 110,
      // Opens the Invoice List page for this invoice — V1's listinvoice, where the
      // charges, the paperwork state and the reasons behind the Errors column all sit
      // together. The link carries `invhistoryrecno` because that resolves without
      // ambiguity; the page accepts several other identities for callers that have
      // something else (see invoiceList.service).
      //
      // NEW TAB, like the pro links. Navigating away loses the bucket you were working
      // — which bucket is selected, the errors-only filter, the selection you had built
      // up — and the back button does not bring it back. Looking an invoice up is a
      // detour from working a queue, not a departure from it.
      //
      // A real anchor rather than a button-plus-window.open, so middle-click,
      // cmd-click, "Open in new tab" and hover-to-see-the-URL all work by themselves.
      cell: (i) => {
        const r = i.row.original;
        if (!r.invoicenumber) return null;
        return (
          <a
            href={invoiceListPath({ invhistoryrecno: r.invhistoryrecno })}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            title="Open the invoice in a new tab"
            className="text-left text-blue-600 dark:text-blue-400 hover:underline"
          >
            {r.invoicenumber}
          </a>
        );
      },
    },
    { accessorKey: 'invoicedate', header: 'Invoice Date', size: 100 },
    {
      id: 'pronumbers',
      header: 'Pro #',
      size: 130,
      accessorFn: (r) => r.pronumbers.join(', '),
      // One pro opens the order drawer, as everywhere else a pro is clickable. Several
      // open one search holding all of them: an invoice_history folds several invoices
      // and so several pros, and opening those one tab at a time is how you lose track
      // of which you have looked at.
      cell: (i) => {
        const pros = i.row.original.pronumbers;
        if (!pros.length) return null;
        const single = pros.length === 1;
        return (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (single) setDrawerPro(Number(pros[0]));
              else openProSearch(pros);
            }}
            title={single
              ? 'Open the order'
              : `Open all ${pros.length} pros on this invoice in one search`}
            className="tabular-nums text-left text-blue-600 dark:text-blue-400 hover:underline"
          >
            {pros.join(', ')}
          </button>
        );
      },
    },
    { accessorKey: 'billtoname', header: 'Bill To', size: 170 },
    { accessorKey: 'mas90code', header: 'Sage Tag', size: 90 },
    { accessorKey: 'bol', header: 'Bol/Bkg/Ref#', size: 150 },
    { accessorKey: 'movetype', header: 'Move Type', size: 100 },
    { accessorKey: 'billtorefno', header: 'Bill To Ref', size: 120 },
    {
      accessorKey: 'invoicetotal',
      header: 'Invoice Amount',
      size: 120,
      cell: (i) => <span className="tabular-nums">{money(i.row.original.invoicetotal)}</span>,
    },
    {
      id: 'errors',
      accessorKey: 'errors',
      header: 'Errors',
      size: 240,
      // The reasons, not just the count. V1 put them in a `title=` on a red cell, which
      // meant hovering one row at a time to find out why a bucket would not send.
      cell: (i) => {
        const r = i.row.original;
        if (!r.errors) return <span className="text-muted">—</span>;
        return (
          <span
            title={r.errormessages.join('\n')}
            className="inline-flex items-start gap-1.5 text-xs text-red-700 dark:text-red-300"
          >
            <span className="shrink-0 mt-0.5 inline-flex items-center rounded px-1.5 py-0.5 font-semibold bg-red-100 dark:bg-red-900/40">
              {r.errors}
            </span>
            <span className="line-clamp-2">{r.errormessages.join('; ')}</span>
          </span>
        );
      },
    },
    {
      id: 'resend',
      accessorKey: 'resend',
      header: 'Resend',
      size: 80,
      cell: (i) =>
        i.row.original.resend === 'Y' ? (
          <span
            title="Flagged to go out again — cancel the Resend on the Invoice screen to remove it from EDI"
            className="inline-flex items-center rounded px-1.5 py-0.5 text-xs font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
          >
            Resend
          </span>
        ) : (
          <span className="text-muted">—</span>
        ),
    },
  ], []);

  const total = useMemo(() => buckets.reduce((s, b) => s + b.count, 0), [buckets]);

  return (
    <div className="p-4 flex flex-col gap-3 h-full min-h-0">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-label">EDI Pending Invoices</h1>
          <p className="text-sm text-muted">
            {total.toLocaleString()} invoice{total === 1 ? '' : 's'} waiting to go out across{' '}
            {buckets.filter((b) => b.count > 0).length} of {buckets.length} partners
          </p>
        </div>
        <Toggle
          variant="with-label"
          size="sm"
          name="hide-empty-buckets"
          checked={hideEmpty}
          onChange={setHideEmpty}
          label="Hide empty buckets"
        />
      </div>

      {bucketsError && (
        <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-sm text-red-800 dark:text-red-200">
          {bucketsError}
        </div>
      )}

      <div className="flex gap-4 flex-1 min-h-0">
        <EdiInvoiceBucketPanel
          buckets={buckets}
          loading={bucketsLoading}
          onSelect={openBucket}
          selectedKey={selectedBucket}
          hideEmpty={hideEmpty}
          validation={validation}
          className="w-72 shrink-0"
        />

        <div className="flex-1 min-w-0 flex flex-col gap-2">
          {!selectedBucket && (
            <div className="flex-1 flex items-center justify-center rounded-lg border border-dashed border-gray-300 dark:border-gray-700 text-sm text-muted">
              Pick a bucket to see the invoices waiting in it.
            </div>
          )}

          {selectedBucket && (
            <>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <h2 className="text-lg font-semibold text-label">
                    {results?.bucket.label ?? selectedBucket}
                  </h2>
                  {results?.bucket.note && (
                    <p className="text-xs text-muted">{results.bucket.note}</p>
                  )}
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-label">
                    Total{' '}
                    <span className="font-semibold tabular-nums">{money(results?.totalamount ?? 0)}</span>
                  </span>
                  <span className="text-label" title="Total of the invoices with no blocking errors">
                    Sendable{' '}
                    <span className="font-semibold tabular-nums">{money(results?.totalclean ?? 0)}</span>
                  </span>
                  {errorCount > 0 && (
                    <Toggle
                      variant="with-label"
                      size="sm"
                      name="errors-only"
                      checked={errorsOnly}
                      onChange={setErrorsOnly}
                      label={`Errors only (${errorCount})`}
                    />
                  )}
                </div>
              </div>

              {/* Selection bar — V1's "Total Selected" plus the actions. Only present
                  when something is selected, so it never competes with the totals. */}
              {selectedRows.length > 0 && (
                <div className="flex items-center justify-between gap-3 flex-wrap rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 px-3 py-2">
                  <span className="text-sm text-blue-900 dark:text-blue-200">
                    <span className="font-semibold">{selectedRows.length}</span> selected ·{' '}
                    <span className="font-semibold tabular-nums">{money(selectedTotal)}</span>
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={clearSelection}
                      className="rounded border border-blue-300 dark:border-blue-700 bg-white dark:bg-gray-900 px-3 py-1 text-sm font-medium text-blue-800 dark:text-blue-200 hover:bg-blue-100 dark:hover:bg-blue-900/40"
                    >
                      Clear
                    </button>
                    <button
                      type="button"
                      onClick={doPreview}
                      disabled={downloading || sending || removing}
                      title="Build the file and view it — nothing is sent or recorded"
                      className="rounded border border-blue-300 dark:border-blue-700 bg-white dark:bg-gray-900 px-3 py-1 text-sm font-medium text-blue-800 dark:text-blue-200 hover:bg-blue-100 dark:hover:bg-blue-900/40 disabled:opacity-50"
                    >
                      {downloading ? 'Building…' : 'View File'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmSend(true)}
                      disabled={sending || removing}
                      className="rounded border border-blue-600 bg-blue-600 px-3 py-1 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                    >
                      {sending ? 'Sending…' : 'Send EDI'}
                    </button>
                    <button
                      type="button"
                      onClick={requestRemove}
                      disabled={sending || removing}
                      className="rounded border border-red-300 dark:border-red-700 bg-white dark:bg-gray-900 px-3 py-1 text-sm font-medium text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-50"
                    >
                      {removing ? 'Removing…' : 'Remove EDI'}
                    </button>
                  </div>
                </div>
              )}

              {/* What the last Remove actually did — both halves. A skip that is not shown
                  is how an invoice quietly stays in the queue. */}
              {removeResult && (
                <div className="flex items-start gap-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 px-3 py-2 text-xs text-label">
                  <div className="flex-1">
                    <span className="font-semibold">
                      Removed {removeResult.removed.length} invoice
                      {removeResult.removed.length === 1 ? '' : 's'} from EDI
                    </span>
                    {removeResult.notes_written > 0 && (
                      <span className="text-muted">
                        {' '}· {removeResult.notes_written} pro note
                        {removeResult.notes_written === 1 ? '' : 's'} written
                      </span>
                    )}
                    {removeResult.skipped.length > 0 && (
                      <ul className="mt-1 space-y-0.5 text-amber-800 dark:text-amber-300">
                        {removeResult.skipped.map((s) => (
                          <li key={s.invhistoryrecno}>
                            Skipped {s.invoicenumber || s.invhistoryrecno}: {s.reason}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <button
                    type="button"
                    aria-label="Dismiss"
                    onClick={() => setRemoveResult(null)}
                    className="shrink-0 text-muted hover:text-label"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              )}

              {/* What the send actually did. Every part of this is a case an operator has
                  to see: a file can be built and deliberately NOT transmitted on a test
                  system, one file of a batch can fail while the others land, and an
                  invoice can be held back by validation or an unmapped charge code. */}
              {sendResult && (
                <div className="flex items-start gap-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 px-3 py-2 text-xs text-label">
                  <div className="flex-1 space-y-1">
                    <span className="font-semibold">
                      Sent {sendResult.sent.length} invoice{sendResult.sent.length === 1 ? '' : 's'}
                      {sendResult.files.length > 1 && ` in ${sendResult.files.length} files`}
                    </span>
                    {sendResult.notesWritten > 0 && (
                      <span className="text-muted">
                        {' '}· {sendResult.notesWritten} pro note{sendResult.notesWritten === 1 ? '' : 's'} written
                      </span>
                    )}
                    <ul className="space-y-0.5">
                      {sendResult.files.map((f) => (
                        <li key={f.filename} className={f.error
                          ? 'text-red-700 dark:text-red-300'
                          : f.transmitted ? '' : 'text-amber-800 dark:text-amber-300'}>
                          <span className="font-mono">{f.filename}</span>{' '}
                          · {f.invoiceCount} invoice{f.invoiceCount === 1 ? '' : 's'}
                          {f.error
                            ? ` · FAILED: ${f.error}`
                            : f.transmitted
                              ? ' · transmitted'
                              : ` · not transmitted (${f.transmitReason})`}
                        </li>
                      ))}
                    </ul>
                    {sendResult.unmappedLineItems.length > 0 && (
                      <p className="text-amber-800 dark:text-amber-300">
                        Held back — no charge code for this partner:{' '}
                        {sendResult.unmappedLineItems.join(', ')}
                      </p>
                    )}
                    {sendResult.blocked.length > 0 && (
                      <ul className="space-y-0.5 text-amber-800 dark:text-amber-300">
                        {sendResult.blocked.map((b) => (
                          <li key={b.invhistoryrecno}>
                            Not sent — {b.invoicenumber}: {b.reasons.join('; ')}
                          </li>
                        ))}
                      </ul>
                    )}
                    {/* Intelligent Audit only: the paperwork bundle that goes with each
                        invoice, on its own FTP session after the file. Summarised rather
                        than listed one line per invoice — a send is routinely hundreds —
                        but anything that failed or went out short is named. */}
                    {!!sendResult.bundles?.length && (
                      <PaperworkSummary bundles={sendResult.bundles} />
                    )}
                  </div>
                  <button
                    type="button"
                    aria-label="Dismiss"
                    onClick={() => setSendResult(null)}
                    className="shrink-0 text-muted hover:text-label"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              )}

              {/* The count-vs-list gap V1 left silent: storage invoices are in the bucket
                  number but can never be sent, so they are not in the list. */}
              {!!results?.storage_excluded && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-900 dark:text-amber-200">
                  <ExclamationTriangleIcon className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>
                    {results.storage_excluded} storage invoice
                    {results.storage_excluded === 1 ? '' : 's'} (ST…) are counted in this bucket but
                    cannot be sent over EDI, so they are not listed.
                  </span>
                </div>
              )}

              {/* Should never appear. If it does, the list is under-validated and saying so
                  is better than showing a falsely clean bucket. */}
              {!!results?.skipped_rules?.length && (
                <div className="flex items-start gap-2 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-xs text-red-900 dark:text-red-200">
                  <ExclamationTriangleIcon className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>
                    Under-validated: {results.skipped_rules.join(', ')} could not be checked because
                    an input was not loaded. Treat a clean row here with suspicion.
                  </span>
                </div>
              )}

              <div className="flex-1 min-h-0">
                <TanTable<EdiInvoiceRow>
                  data={rows}
                  columns={columns}
                  loading={resultsLoading}
                  errorMessage={resultsError}
                  onRetry={() => selectedBucket && loadResults(selectedBucket)}
                  settingsKey="pending-edi-invoices-table"
                  cardTitle={`${results?.bucket.label ?? ''} — ${rows.length} invoice${rows.length === 1 ? '' : 's'}`}
                  searchPlaceholder="Search invoice #, bill to, BOL, ref…"
                  enableColumnFilters
                  enableRowSelection
                  // Invoice # stays visible while the grid scrolls sideways (it is what
                  // every row is identified by), and the checkboxes sit at the right edge
                  // next to the Remove action rather than leading the row.
                  lockedColumns={[
                    { id: 'invoicenumber', side: 'left' },
                    { id: SELECT_COLUMN_ID, side: 'right' },
                  ]}
                  // Keyed by invoice history recno — the id the Remove action posts, and
                  // what lets a selection survive a re-render of the same rows.
                  getRowId={(r) => String(r.invhistoryrecno)}
                  onSelectionChange={(sel) =>
                    setSelectedIds(sel.map((r) => String(r.invhistoryrecno)))
                  }
                  selectionResetKey={selectionResetKey}
                  getRowClassName={(row: EdiInvoiceRow, m: { zebra: boolean }) =>
                    row.errors > 0
                      ? 'bg-red-50 dark:bg-red-900/20'
                      : m.zebra
                        ? 'bg-gray-50 dark:bg-gray-800/40'
                        : ''
                  }
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Send is confirmed, like Remove, because it is not undoable: the file reaches
          the partner and the invoices leave the queue. The copy says which of those two
          will actually happen, since a test system builds the file without sending it. */}
      <ProDetailSlideOver
        pronumber={drawerPro}
        isOpen={drawerPro !== null}
        onClose={closeProDrawer}
      />

      <EdiInvoiceFileViewer
        open={!!preview}
        onClose={() => setPreview(null)}
        bucketLabel={preview?.bucket.label ?? ''}
        files={preview?.files ?? []}
        blockedCount={preview?.blocked.length ?? 0}
        unmappedLineItems={preview?.unmappedLineItems ?? []}
      />

      <WarningConfirmModal
        open={confirmSend}
        title="Send over EDI"
        message={
          <>
            Send {selectedRows.length} invoice{selectedRows.length === 1 ? '' : 's'} (
            {money(selectedTotal)}) to {results?.bucket.label ?? 'this partner'}?
            <div className="mt-2 text-xs text-muted">
              The invoice file is built, archived and transmitted, the invoices leave this
              bucket, and a note is added to each pro. Invoices with errors are held back
              and listed afterwards.
            </div>
          </>
        }
        confirmLabel="Send EDI"
        cancelLabel="Cancel"
        onConfirm={doSend}
        onCancel={() => setConfirmSend(false)}
      />

      <WarningConfirmModal
        open={confirmOpen}
        title="Remove from EDI"
        message={
          <>
            Remove {selectedRows.length} invoice{selectedRows.length === 1 ? '' : 's'} (
            {money(selectedTotal)}) from EDI?
            <div className="mt-2 text-xs text-muted">
              They will be marked do-not-send, leave this bucket, and a note is added to each
              pro. Undoing it means clearing the flag on the Invoice screen.
            </div>
          </>
        }
        confirmLabel="Remove EDI"
        cancelLabel="Cancel"
        onConfirm={doRemove}
        onCancel={() => setConfirmOpen(false)}
      />

      <AlertModal
        open={!!notice}
        title={notice?.title}
        message={notice?.message}
        onClose={() => setNotice(null)}
      />
    </div>
  );
};

export default PendingEdiInvoices;
